'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';
import { BrandText } from '@/components/marketing/BrandText';
import { captureAffiliateSourceFromSearch } from '@/lib/marketing/affiliateSource';


interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  officeLocation: string;
  services: string[];
  message: string;
  affiliateSource?: string;
  /** Honeypot — ostavi prazno */
  website?: string;
}

export default function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [affiliateSource, setAffiliateSource] = useState('');
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ContactFormData>({
    defaultValues: { services: [], officeLocation: '', phone: '', affiliateSource: '', website: '' },
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const source = captureAffiliateSourceFromSearch(window.location.search);
    setAffiliateSource(source);
    setValue('affiliateSource', source, { shouldDirty: false });
  }, [setValue]);

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        toast.error(t('contact.errorSend'));
        return;
      }
      setSubmitted(true);
      toast.success(t('contact.toast'));
      reset();
    } catch {
      toast.error(t('contact.errorSend'));
    } finally {
      setSubmitting(false);
    }
  };

  const SERVICES_OPTIONS = [
    { id: 'vo', key: 'contact.services.vo' },
    { id: 'cw', key: 'contact.services.cw' },
    { id: 'acc', key: 'contact.services.acc' },
    { id: 'reg', key: 'contact.services.reg' },
    { id: 'gen', key: 'contact.services.gen' },
  ] as const;

  const CONTACT_DETAILS = [
    { icon: MapPin, labelKey: 'contact.address', valueKey: 'contact.addressValue' },
    { icon: Phone, labelKey: 'contact.phone', valueKey: 'contact.phoneValue' },
    { icon: Mail, labelKey: 'contact.email', valueKey: 'contact.emailValue' },
    { icon: Clock, labelKey: 'contact.hours', valueKey: 'contact.hoursValue' },
  ];

  const LANGUAGES_DISPLAY = [
    { flag: '🇷🇸', label: 'Srpski' },
    { flag: '🇬🇧', label: 'English' },
    { flag: '🇷🇺', label: 'Русский' },
    { flag: '🇹🇷', label: 'Türkçe' },
    { flag: '🇩🇪', label: 'Deutsch' },
  ];

  return (
    <section id="contact" className="py-20 lg:py-28 bg-slate-50">
      <Toaster position="bottom-right" richColors />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('contact.tag')}</p>
          <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight mb-4">
            <BrandText text={t('contact.title')} />
          </h2>
          <p className="text-slate-500 text-lg leading-relaxed max-w-xl mx-auto">
            {t('contact.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Map placeholder */}
            <div className="relative rounded-xl overflow-hidden bg-[#1B3A5C] h-44 flex items-center justify-center border border-[#1B3A5C]">
              <div className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
                  backgroundSize: '30px 30px',
                }}
              />
              <div className="relative z-10 text-center">
                <div className="w-12 h-12 rounded-full bg-[#C9A96E] flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <MapPin size={22} className="text-white" />
                </div>
                <p className="text-white font-semibold text-sm">{t('contact.mapStreet')}</p>
                <p className="text-slate-300 text-xs mt-0.5">{t('contact.mapCity')}</p>
              </div>
            </div>

            {/* Contact details */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-display font-bold text-[#1B3A5C] text-base mb-5">{t('contact.tag')}</h3>
              <div className="space-y-4">
                {CONTACT_DETAILS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={`contact-info-${item.labelKey}`} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3A5C]/8 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={15} className="text-[#1B3A5C]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium">{t(item.labelKey)}</p>
                        <p className="text-sm text-slate-700">{t(item.valueKey!)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Response time + languages */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <MessageSquare size={14} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700">{t('contact.responseTime')}</p>
                  <p className="text-xs text-emerald-600 font-semibold">{t('contact.responseValue')}</p>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 mb-2.5">{t('contact.weRespond')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {LANGUAGES_DISPLAY.map((lang) => (
                    <span key={`lang-badge-${lang.label}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                      {lang.flag} {lang.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-7">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                    <CheckCircle size={32} className="text-emerald-500" />
                  </div>
                  <h3 className="font-display font-bold text-[#1B3A5C] text-xl mb-2">{t('contact.successTitle')}</h3>
                  <p className="text-slate-500 text-sm max-w-xs">
                    {t('contact.successDesc')}
                  </p>
                  <button onClick={() => setSubmitted(false)} className="btn-secondary mt-6 text-sm">
                    {t('contact.sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  {/* Honeypot: ne uklanjati — skriveno od korisnika, botovi ga često popune */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="contact-form-affiliate-source">Affiliate source</label>
                    <input
                      id="contact-form-affiliate-source"
                      type="hidden"
                      tabIndex={-1}
                      defaultValue={affiliateSource}
                      {...register('affiliateSource')}
                    />
                    <label htmlFor="contact-form-website-hp">Website</label>
                    <input
                      id="contact-form-website-hp"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register('website')}
                    />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-[#1B3A5C] text-lg mb-1">{t('contact.formTitle')}</h3>
                    <p className="text-sm text-slate-400">{t('contact.formDesc')}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label-base">{t('contact.name')}</label>
                      <input
                        {...register('name', { required: t('contact.errName') })}
                        className="input-base"
                        placeholder={t('contact.placeholderName')}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="label-base">{t('contact.emailField')}</label>
                      <input
                        type="email"
                        {...register('email', {
                          required: t('contact.errEmail'),
                          pattern: { value: /^\S+@\S+\.\S+$/, message: t('contact.errEmailInvalid') },
                        })}
                        className="input-base"
                        placeholder={t('contact.placeholderEmail')}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="label-base">{t('contact.phoneField')}</label>
                    <input
                      type="tel"
                      autoComplete="tel"
                      {...register('phone', {
                        required: t('contact.errPhone'),
                        validate: (v) => {
                          const raw = typeof v === 'string' ? v.trim() : '';
                          const digits = raw.replace(/\D/g, '').length;
                          if (raw.length < 8 || digits < 8) return t('contact.errPhone');
                          return true;
                        },
                      })}
                      className="input-base"
                      placeholder={t('contact.placeholderPhone')}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label-base">{t('contact.company')}</label>
                      <input
                        {...register('company')}
                        className="input-base"
                        placeholder={t('contact.placeholderCompany')}
                      />
                    </div>
                    <div>
                      <label className="label-base">{t('contact.country')}</label>
                      <input
                        {...register('country')}
                        className="input-base"
                        placeholder={t('contact.placeholderCountry')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label-base">{t('contact.officeLocation')}</label>
                    <select
                      {...register('officeLocation', { required: t('contact.errOfficeLocation') })}
                      className="input-base"
                    >
                      <option value="">{t('contact.selectOfficeLocation')}</option>
                      <option value="belgrade-savski">{t('contact.location.belgrade')}</option>
                      <option value="kovin-pu-pancevo">{t('contact.location.kovinPancevo')}</option>
                      <option value="loznica">{t('contact.location.loznica')}</option>
                    </select>
                    {errors.officeLocation && (
                      <p className="text-red-500 text-xs mt-1">{errors.officeLocation.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label-base">{t('contact.service')}</label>
                    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-1">
                      {SERVICES_OPTIONS.map((s, i) => (
                        <label
                          key={s.id}
                          className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 rounded-md px-2 py-1.5 -mx-1 hover:bg-white/80"
                        >
                          <input
                            type="checkbox"
                            value={s.id}
                            {...register(
                              'services',
                              i === 0
                                ? {
                                    validate: (v) =>
                                      (Array.isArray(v) && v.length > 0) || t('contact.serviceRequired'),
                                  }
                                : {},
                            )}
                            className="size-4 shrink-0 rounded border-slate-300 text-[#1B3A5C] focus:ring-[#1B3A5C]"
                          />
                          <span>{t(s.key)}</span>
                        </label>
                      ))}
                    </div>
                    {errors.services && <p className="text-red-500 text-xs mt-1">{errors.services.message}</p>}
                  </div>

                  <div>
                    <label className="label-base">{t('contact.message')}</label>
                    <p className="text-xs text-slate-400 mb-1.5">{t('contact.messageHint')}</p>
                    <textarea
                      {...register('message', {
                        required: t('contact.errMessage'),
                        minLength: { value: 20, message: t('contact.errMessageMin') },
                      })}
                      className="input-base resize-none"
                      rows={4}
                      placeholder={t('contact.placeholderMessage')}
                    />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`btn-primary w-full justify-center text-sm py-3 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('contact.sending')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={15} />
                        {t('contact.send')}
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}